import React, { createContext, forwardRef, useImperativeHandle } from "react";
import { type KeywordFilterType, initialKeywordFilter } from "./Filters/FilterTypes/KeywordFilterType";
import { type FavoriteArtistFilterType, initialFavoriteArtistFilter } from "./Filters/FilterTypes/FavoriteArtistFilterType";
import SelectedFilter from "./SelectedFilter";
import { type ArtistProfileFilterType, initialArtistProfileFilter } from "./Filters/FilterTypes/ArtistProfileFilterType";
import { type TagFilterType, initialTagFilter } from "./Filters/FilterTypes/TagFilterType";
import { type VenueCapacityFilterType, initialVenueCapacityFilter } from "./Filters/FilterTypes/VenueCapacityFilterType";
import { type VenueCategoryFilterType, initialVenueCategoryFilter } from "./Filters/FilterTypes/VenueCategoryFilterType";
import { type StatusFilterType, initialStatusFilter } from "./Filters/FilterTypes/StatusFilterType";
import { type OwnersFilterType, initialOwnersFilter } from "./Filters/FilterTypes/OwnerFilterType";
import { type DataClearedFilterType, initialDataClearedFilter } from "./Filters/FilterTypes/DataClearedFilterType";
import { type FunctionGroupFilterType, initialFunctionGroupFilter } from "./Filters/FilterTypes/FunctionGroupFilterType";
import { type OnlyMySelectionFilterType, initialOnlyMySelectionFilter } from "./Filters/FilterTypes/OnlyMySelectionFilterType";
import { type AddressFilterType, initialAddressFilter } from "./Filters/FilterTypes/AddressFilterType";
import { type FinancialRiskFilterType, initialFinancialRiskFilter } from "./Filters/FilterTypes/FinancialRiskFilterType";
import { type CompanySortFilterType, initialCompanySortFilter } from "./Filters/FilterTypes/CompanySortFilterType";
import { type ContactSortFilterType, initialContactSortFilter } from "./Filters/FilterTypes/ContactSortFilterType";
import { type InvoiceSortFilterType, initialInvoiceSortFilter } from "./Filters/FilterTypes/InvoiceSortFilterType";
import { type DocumentSortFilterType, initialDocumentSortFilter } from "./Filters/FilterTypes/DocumentSortFilterType";
import { type PaymentSortFilterType, initialPaymentSortFilter } from "./Filters/FilterTypes/PaymentSortFilterType";
import { type ShowSortFilterType, initialShowSortFilter } from "./Filters/FilterTypes/ShowSortFilterType";
import { type ArtistFilterType, initialArtistFilter } from "./Filters/FilterTypes/ArtistFilterType";
import { type ContactFilterType, initialContactFilter } from "./Filters/FilterTypes/ContactFilterType";
import { type AppointmentPhaseFilterType, initialAppointmentPhaseFilter } from "./Filters/FilterTypes/AppointmentPhaseFilterType";
import { type ExtractorSessionLifeCycleFilterType, initialExtractorSessionLifeCycleFilter } from "./Filters/FilterTypes/ExtractorSessionLifeCycleFilterType";
import { type PeriodFilterType, initialPeriodFilter } from "./Filters/FilterTypes/PeriodFilterType";
import { type ProductionFilterType, initialProductionFilter } from "./Filters/FilterTypes/ProductionFilterType";
import { type BookkeepingFilterType, initialBookkeepingFilter } from "./Filters/FilterTypes/BookkeepingFilterType";
import { type CompanyFilterType, initialCompanyFilter } from "./Filters/FilterTypes/CompanyFilterType";
import { type PaymentMethodFilterType, initialPaymentMethodFilter } from "./Filters/FilterTypes/PaymentMethodFilterType";
import { type CultureFilterType, initialCultureFilter } from "./Filters/FilterTypes/CultureFilterType";
import { type InvoiceProgressFilterType, initialInvoiceProgressFilter } from "./Filters/FilterTypes/InvoiceProgressFilterType";
import { type CostCenterFilterType, initialCostCenterFilter } from "./Filters/FilterTypes/CostCenterFilterType";
import { type DocumentProgressFilterType, initialDocumentProgressFilter } from "./Filters/FilterTypes/DocumentProgressFilterType";
import { type HotelFilterType, initialHotelFilter } from "./Filters/FilterTypes/HotelFilterType";
import { type PromoterFilterType, initialPromoterFilter } from "./Filters/FilterTypes/PromoterFilterType";
import { type VenueFilterType, initialVenueFilter } from "./Filters/FilterTypes/VenueFilterType";
import { type PublicationFilterType, initialPublicationFilter } from "./Filters/FilterTypes/PublicationFilterType";
import { type VisibleFilterType, initialVisibleFilter } from "./Filters/FilterTypes/VisibleFilterType";
import { type ProductionSortFilterType, initialProductionSortFilter } from "./Filters/FilterTypes/ProductionSortFilterType";
import { type AppointmentTypeFilterType, initialAppointmentTypeFilter } from "./Filters/FilterTypes/AppointmentTypeFilterType";
import { type WeekFilterType, initialWeekFilter } from "./Filters/FilterTypes/WeekFilterType";
import { type MonthFilterType, initialMonthFilter } from "./Filters/FilterTypes/MonthFilterType";
import { type QuarterFilterType, initialQuarterFilter } from "./Filters/FilterTypes/QuarterFilterType";
import { type ProductGroupFilterType, initialProductGroupFilter } from "./Filters/FilterTypes/ProductGroupFilterType";
import { type FreefieldProfileFilterType, initialFreefieldProfileFilter } from "./Filters/FilterTypes/FreefieldProfileFilterType";
import { type UserToClientLifecycleFilterType, initialUserToClientLifecycleOption } from "./Filters/FilterTypes/UserToClientLifecycleFilterType";
import { type AirportSizeFilterType, initialAirportSizeFilter } from "./Filters/FilterTypes/AirportSizeFilterType";
import { type ClientFilterType, initialClientFilter } from "./Filters/FilterTypes/ClientFilterType";
import { type EnvironmentFilterType, initialEnvironmentFilter } from "./Filters/FilterTypes/EnvironmentFilterType";
import { type TaskManagerSortFilterType, initialTaskManagerSortFilter } from "./Filters/FilterTypes/TaskSortFilterType";
import { type TaskManagerPeriodFilterType, initialTaskManagerPeriodFilter } from "./Filters/FilterTypes/TaskManagerPeriodFilterType";
import { type ClientFileSortFilterType, initialClientFileSortFilter } from "./Filters/FilterTypes/ClientFileSortFilterType";
import { type NotificationStatusFilterType, initialNotificationStatusFilter } from "./Filters/FilterTypes/NotificationStatusFilterType";
import { type ClientSortFilterType, initialClientSortFilter } from "./Filters/FilterTypes/ClientSortFilterType";
import { type ClientAccessFilterType, initialClientAccessFilter } from "./Filters/FilterTypes/ClientAccessFilterType";
import { type HubSpotLinkedFilterType, initialHubSpotLinkedFilter } from "./Filters/FilterTypes/HubSpotLinkedFilterType";
import { type GroupByPeriodFilterType, initialGroupByPeriodFilter } from "./Filters/FilterTypes/GroupByPeriodFilterType";
import { type GuestlistAccessFilterType, initialAccessFilter } from "./Filters/FilterTypes/GuestlistAccessFilterType";
import { initialCountriesFilter, type CountryFilterType } from "./Filters/FilterTypes/CountryFilterType";

type FilterPopupPosition = "left" | "bottom";

interface FilterProviderProps {
  /**
   * Function that is called when the filter values change. The argument **values** is the
   * new filter values. This function is called once for every change in the filter values.
   */
  onFilterChange: (values: FilterValues, filtersWithValues: Partial<FilterValues>) => void
  /**
   * Where the filter popup should be positioned. Default is "bottom". On the map we want it to be on
   *  the left.
   */
  filterPopupPosition?: FilterPopupPosition
}

export interface FilterProviderRef {
  reset: () => void
  updateValues: (func: (prev: FilterValues) => Partial<FilterValues>, setAsDefault?: boolean) => Partial<FilterValues>
  setDefaultValues: (func: (prev: FilterValues) => Partial<FilterValues>) => void
}

export interface FilterValues {
  address: AddressFilterType
  appointmentPhase: AppointmentPhaseFilterType
  appointmentType: AppointmentTypeFilterType
  airportSize: AirportSizeFilterType
  artist: ArtistFilterType
  artistProfile: ArtistProfileFilterType
  bookkeepings: BookkeepingFilterType
  client: ClientFilterType
  clientSort: ClientSortFilterType
  clientAccess: ClientAccessFilterType
  clientFileSort: ClientFileSortFilterType
  company: CompanyFilterType
  companySort: CompanySortFilterType
  contact: ContactFilterType
  contactSort: ContactSortFilterType
  costCenter: CostCenterFilterType
  country: CountryFilterType
  culture: CultureFilterType
  dataCleared: DataClearedFilterType,
  documentProgress: DocumentProgressFilterType
  documentSort: DocumentSortFilterType
  environment: EnvironmentFilterType
  extractorSessionLifeCycle: ExtractorSessionLifeCycleFilterType
  favoriteArtists: FavoriteArtistFilterType
  financialRisks: FinancialRiskFilterType
  functionGroups: FunctionGroupFilterType
  freefieldProfile: FreefieldProfileFilterType
  groupByPeriod: GroupByPeriodFilterType
  guestlistAccessTypes: GuestlistAccessFilterType
  hotel: HotelFilterType
  hubSpotLinked: HubSpotLinkedFilterType
  invoiceProgress: InvoiceProgressFilterType
  invoiceSort: InvoiceSortFilterType
  keyword: KeywordFilterType
  month: MonthFilterType
  notificationStatus: NotificationStatusFilterType
  onlyMySelection: OnlyMySelectionFilterType
  owners: OwnersFilterType
  quarter: QuarterFilterType
  paymentMethod: PaymentMethodFilterType
  paymentSort: PaymentSortFilterType
  period: PeriodFilterType
  productGroup: ProductGroupFilterType
  production: ProductionFilterType
  productionSort: ProductionSortFilterType
  promoter: PromoterFilterType
  publication: PublicationFilterType
  showSort: ShowSortFilterType
  statuses: StatusFilterType
  tags: TagFilterType
  taskManagerPeriod: TaskManagerPeriodFilterType
  taskManagerSort: TaskManagerSortFilterType
  userToClientLifecycle: UserToClientLifecycleFilterType
  venue: VenueFilterType
  venueCapacity: VenueCapacityFilterType
  venueCategory: VenueCategoryFilterType
  visible: VisibleFilterType
  week: WeekFilterType
}

const initialFilterValues: FilterValues = {
  address: initialAddressFilter,
  appointmentPhase: initialAppointmentPhaseFilter,
  appointmentType: initialAppointmentTypeFilter,
  airportSize: initialAirportSizeFilter,
  artist: initialArtistFilter,
  artistProfile: initialArtistProfileFilter,
  bookkeepings: initialBookkeepingFilter,
  client: initialClientFilter,
  clientSort: initialClientSortFilter,
  clientAccess: initialClientAccessFilter,
  clientFileSort: initialClientFileSortFilter,
  company: initialCompanyFilter,
  companySort: initialCompanySortFilter,
  contact: initialContactFilter,
  contactSort: initialContactSortFilter,
  costCenter: initialCostCenterFilter,
  country: initialCountriesFilter,
  culture: initialCultureFilter,
  dataCleared: initialDataClearedFilter,
  documentProgress: initialDocumentProgressFilter,
  documentSort: initialDocumentSortFilter,
  environment: initialEnvironmentFilter,
  extractorSessionLifeCycle: initialExtractorSessionLifeCycleFilter,
  favoriteArtists: initialFavoriteArtistFilter,
  financialRisks: initialFinancialRiskFilter,
  functionGroups: initialFunctionGroupFilter,
  freefieldProfile: initialFreefieldProfileFilter,
  groupByPeriod: initialGroupByPeriodFilter,
  guestlistAccessTypes: initialAccessFilter,
  hotel: initialHotelFilter,
  hubSpotLinked: initialHubSpotLinkedFilter,
  invoiceProgress: initialInvoiceProgressFilter,
  invoiceSort: initialInvoiceSortFilter,
  keyword: initialKeywordFilter,
  month: initialMonthFilter,
  notificationStatus: initialNotificationStatusFilter,
  onlyMySelection: initialOnlyMySelectionFilter,
  owners: initialOwnersFilter,
  paymentMethod: initialPaymentMethodFilter,
  paymentSort: initialPaymentSortFilter,
  period: initialPeriodFilter,
  productGroup: initialProductGroupFilter,
  production: initialProductionFilter,
  productionSort: initialProductionSortFilter,
  promoter: initialPromoterFilter,
  publication: initialPublicationFilter,
  quarter: initialQuarterFilter,
  showSort: initialShowSortFilter,
  statuses: initialStatusFilter,
  tags: initialTagFilter,
  taskManagerPeriod: initialTaskManagerPeriodFilter,
  taskManagerSort: initialTaskManagerSortFilter,
  userToClientLifecycle: initialUserToClientLifecycleOption,
  venue: initialVenueFilter,
  venueCapacity: initialVenueCapacityFilter,
  venueCategory: initialVenueCategoryFilter,
  visible: initialVisibleFilter,
  week: initialWeekFilter,
};

interface FilterDefinition<TFilter> {
  getTitle: (filter: TFilter) => string
  renderSelection: (filter: TFilter) => JSX.Element
  hasValue: (filter: TFilter) => boolean
  areEqual: (a: TFilter, b: TFilter) => boolean
  reset: () => TFilter
}

export interface FilterDefinitions {
  address: FilterDefinition<AddressFilterType>
  appointmentPhase: FilterDefinition<AppointmentPhaseFilterType>
  appointmentType: FilterDefinition<AppointmentTypeFilterType>
  airportSize: FilterDefinition<AirportSizeFilterType>
  artist: FilterDefinition<ArtistFilterType>
  artistProfile: FilterDefinition<ArtistProfileFilterType>
  bookkeepings: FilterDefinition<BookkeepingFilterType>
  client: FilterDefinition<ClientFilterType>
  clientSort: FilterDefinition<ClientSortFilterType>
  clientAccess: FilterDefinition<ClientAccessFilterType>
  clientFileSort: FilterDefinition<ClientFileSortFilterType>
  company: FilterDefinition<CompanyFilterType>
  companySort: FilterDefinition<CompanySortFilterType>
  contact: FilterDefinition<ContactFilterType>
  contactSort: FilterDefinition<ContactSortFilterType>
  costCenter: FilterDefinition<CostCenterFilterType>
  country: FilterDefinition<CountryFilterType>
  culture: FilterDefinition<CultureFilterType>
  dataCleared: FilterDefinition<DataClearedFilterType>
  documentProgress: FilterDefinition<DocumentProgressFilterType>
  documentSort: FilterDefinition<DocumentSortFilterType>
  environment: FilterDefinition<EnvironmentFilterType>
  extractorSessionLifeCycle: FilterDefinition<ExtractorSessionLifeCycleFilterType>
  favoriteArtists: FilterDefinition<FavoriteArtistFilterType>
  financialRisks: FilterDefinition<FinancialRiskFilterType>
  functionGroups: FilterDefinition<FunctionGroupFilterType>
  freefieldProfile: FilterDefinition<FreefieldProfileFilterType>
  groupByPeriod: FilterDefinition<GroupByPeriodFilterType>
  guestlistAccessTypes: FilterDefinition<GuestlistAccessFilterType>
  hotel: FilterDefinition<HotelFilterType>
  hubSpotLinked: FilterDefinition<HubSpotLinkedFilterType>
  invoiceProgress: FilterDefinition<InvoiceProgressFilterType>
  invoiceSort: FilterDefinition<InvoiceSortFilterType>
  keyword: FilterDefinition<KeywordFilterType>
  month: FilterDefinition<MonthFilterType>
  notificationStatus: FilterDefinition<NotificationStatusFilterType>
  onlyMySelection: FilterDefinition<OnlyMySelectionFilterType>
  owners: FilterDefinition<OwnersFilterType>
  quarter: FilterDefinition<QuarterFilterType>
  paymentMethod: FilterDefinition<PaymentMethodFilterType>
  paymentSort: FilterDefinition<PaymentSortFilterType>
  period: FilterDefinition<PeriodFilterType>
  productGroup: FilterDefinition<ProductGroupFilterType>
  production: FilterDefinition<ProductionFilterType>
  productionSort: FilterDefinition<ProductionSortFilterType>
  promoter: FilterDefinition<PromoterFilterType>
  publication: FilterDefinition<PublicationFilterType>
  showSort: FilterDefinition<ShowSortFilterType>
  statuses: FilterDefinition<StatusFilterType>
  tags: FilterDefinition<TagFilterType>
  taskManagerPeriod: FilterDefinition<TaskManagerPeriodFilterType>
  taskManagerSort: FilterDefinition<TaskManagerSortFilterType>
  userToClientLifecycle: FilterDefinition<UserToClientLifecycleFilterType>
  venue: FilterDefinition<VenueFilterType>
  venueCapacity: FilterDefinition<VenueCapacityFilterType>
  venueCategory: FilterDefinition<VenueCategoryFilterType>
  visible: FilterDefinition<VisibleFilterType>
  week: FilterDefinition<WeekFilterType>
}

const intitialFilterDefinitions: FilterDefinitions = {
  address: undefined,
  appointmentPhase: undefined,
  appointmentType: undefined,
  airportSize: undefined,
  artist: undefined,
  artistProfile: undefined,
  bookkeepings: undefined,
  client: undefined,
  clientSort: undefined,
  clientAccess: undefined,
  clientFileSort: undefined,
  company: undefined,
  companySort: undefined,
  contact: undefined,
  contactSort: undefined,
  costCenter: undefined,
  country: undefined,
  culture: undefined,
  dataCleared: undefined,
  documentProgress: undefined,
  documentSort: undefined,
  environment: undefined,
  extractorSessionLifeCycle: undefined,
  favoriteArtists: undefined,
  financialRisks: undefined,
  functionGroups: undefined,
  freefieldProfile: undefined,
  groupByPeriod: undefined,
  guestlistAccessTypes: undefined,
  hotel: undefined,
  hubSpotLinked: undefined,
  invoiceProgress: undefined,
  invoiceSort: undefined,
  keyword: undefined,
  month: undefined,
  notificationStatus: undefined,
  onlyMySelection: undefined,
  owners: undefined,
  quarter: undefined,
  paymentMethod: undefined,
  paymentSort: undefined,
  period: undefined,
  productGroup: undefined,
  production: undefined,
  productionSort: undefined,
  promoter: undefined,
  publication: undefined,
  showSort: undefined,
  statuses: undefined,
  tags: undefined,
  taskManagerPeriod: undefined,
  taskManagerSort: undefined,
  userToClientLifecycle: undefined,
  venue: undefined,
  venueCapacity: undefined,
  venueCategory: undefined,
  visible: undefined,
  week: undefined
};

type FilterTypes = keyof FilterValues;

interface FilterContextValue {
  isInFilterContext: boolean
  noFiltersSet: boolean
  isDefault: boolean
  values: FilterValues
  filterPopupPosition: FilterPopupPosition
  register: <T extends FilterTypes>(key: T, registerOptions: FilterDefinition<FilterValues[T]>) => void
  getValue: <T extends FilterTypes>(key: T) => FilterValues[T]
  setValue: <T extends FilterTypes>(key: T, value: FilterValues[T]) => void
  setInitialValue: <T extends FilterTypes>(key: T, value: FilterValues[T]) => void
  revertToDefault: () => void
  renderSelection: () => JSX.Element
  renderSingleFilterValue: (filterKey: string, showTitle: boolean) => JSX.Element
}

const defaultFilterContext: FilterContextValue = {
  isInFilterContext: false,
  noFiltersSet: true,
  isDefault: true,
  values: initialFilterValues,
  filterPopupPosition: "bottom",
  register: () => null,
  getValue: () => null,
  setValue: () => { },
  setInitialValue: () => { },
  revertToDefault: () => { },
  renderSelection: () => null,
  renderSingleFilterValue: () => null
};

export const FilterContext = createContext<FilterContextValue>(defaultFilterContext);

export const FilterProvider = forwardRef((props: React.PropsWithChildren<FilterProviderProps>, ref) => {
  const { onFilterChange } = props;

  // Current values of all filters
  const [values, setValues] = React.useState<FilterValues>(initialFilterValues);

  const [defaultFilter, setDefaultFilter] = React.useState<Partial<FilterValues>>(initialFilterValues);
  const defaultFilterValues = { ...initialFilterValues, ...defaultFilter };

  // Intialized filters. This is needed to make sure every filter is only initialized once. In some cases
  //  filters get hidden and then show again (example: planning map), and this causes a re-initialization of the filter
  const initializedFiltersRef = React.useRef<FilterTypes[]>([]);
  const initializedFilters = initializedFiltersRef.current;

  // Filter definitions. Here we use a ref since we don't need this in a state, as it never changes
  const defsRef = React.useRef<FilterDefinitions>(intitialFilterDefinitions);
  const defs = defsRef.current;
  const definedFiltersRef = React.useRef<string[]>([]);
  const definedFilters = definedFiltersRef.current;
  const noFiltersSet = definedFilters.every(key => !defs[key].hasValue(values[key]));

  useImperativeHandle(ref, () => ({
    // Expose a function to set new values for the filters. This does not tigger a filter change
    updateValues: (func: (prev: FilterValues) => Partial<FilterValues>) => {
      const newValues = func(values);
      setValues(prev => ({ ...prev, ...newValues }));
      return getFilterWithValues({ ...values, ...newValues });
    },
    setDefaultValues: (func: (prev: FilterValues) => Partial<FilterValues>) => {
      const newDefault = func(values);
      setDefaultFilter(prev => ({ ...prev, ...newDefault }));
    },
    // Reset the filter. This does trigger a filter change with the reset values
    reset: () => {
      const newValues = { ...values };
      definedFilters.forEach(key => {
        const def = defs[key];
        newValues[key] = def.reset();
      });
      onFilterChange(newValues, getFilterWithValues(newValues));
      setValues(newValues);
    }
  }));

  const getFilterWithValues = (vals: FilterValues) => {
    const filterKeys = definedFilters.filter(key => defs[key].hasValue(vals[key]));
    return filterKeys.reduce<Partial<FilterValues>>((acc, key) => {
      acc[key] = vals[key];
      return acc;
    }, {});
  };

  const areEqual = (a: FilterValues, b: FilterValues, defs: FilterDefinitions) => {
    return definedFilters.every(<T extends FilterTypes>(key: T) => {
      const filterA = a[key];
      const filterB = b[key];
      const def = defs[key];
      if (filterA === undefined && filterB === undefined) { return true; }
      if (filterA === undefined || filterB === undefined) { return false; }
      return def.areEqual(filterA as any, filterB as any);
    });
  };

  const register = <T extends FilterTypes>(key: T, registerOptions: FilterDefinition<FilterValues[T]>) => {
    if (defs[key] === undefined) {
      defsRef.current = {
        ...defsRef.current,
        [key]: registerOptions
      };
      definedFiltersRef.current.push(key);
    }
  };

  const getValue = <T extends FilterTypes>(key: T): FilterValues[T] => {
    return values[key];
  };

  // Set the initial value of a filter. The difference between this and setValue is that this sets the default value as well
  //  and doesn't trigger a filter change
  const setInitialValue = <T extends FilterTypes>(key: T, value: FilterValues[T]) => {
    if (!initializedFilters.includes(key)) {
      setValues(prev => ({ ...prev, [key]: value }));
      setDefaultFilter(prev => ({ ...prev, [key]: value }));
      initializedFiltersRef.current.push(key);
    }
  };

  // Set the value of a filter. If the value is different from the current value, trigger a filter change
  const setValue = <T extends FilterTypes>(key: T, value: FilterValues[T]) => {
    const newValues = {
      ...values,
      [key]: value
    };
    if (!areEqual(values, newValues, defs)) {
      onFilterChange(newValues, getFilterWithValues(newValues));
      setValues(newValues);
    }
  };

  const isDefault = areEqual(values, defaultFilterValues, defs);

  const revertToDefault = () => {
    if (!isDefault) {
      const newValues = {
        ...values,
        ...defaultFilterValues
      };
      if (!areEqual(values, newValues, defs)) {
        onFilterChange(newValues, getFilterWithValues(newValues));
        setValues(newValues);
      }
    }
  };

  const renderSingleFilterValue = (filterKey: string, showTitle: boolean) => {
    const def = defs[filterKey];
    const value = values[filterKey];

    if (!def || !def.hasValue(value)) {
      return null;
    }

    return <SelectedFilter
      showTitle={showTitle}
      key={filterKey}
      title={def.getTitle(value)}
      value={value}
      removeFilter={() => { setValue(filterKey as FilterTypes, def.reset()); }}
      renderTemplate={() => def.renderSelection(value)}
    />;
  }

  const renderSelection = () => {
    if (noFiltersSet) {
      return <div className="text-sublabel">{RESX.SelectionManager.NoFiltersApplied}</div>;
    }
    return <>{definedFilters.map(f => renderSingleFilterValue(f, true))}</>;
  };

  return <FilterContext.Provider value={{
    isInFilterContext: true,
    noFiltersSet,
    isDefault,
    values,
    filterPopupPosition: props.filterPopupPosition || "bottom",
    register,
    getValue,
    setInitialValue,
    setValue,
    revertToDefault,
    renderSelection,
    renderSingleFilterValue
  }}>
    {props.children}
  </FilterContext.Provider>;
});
